import type {
  CatalogueAdministrationCapabilities,
  CatalogueDraftCommand,
  CatalogueTransitionCommand,
  CatalogueVersionPreview,
  CatalogueVersionPage,
  CatalogueVersionState,
} from "@reminder-tracker/backend-contracts";
import type { AdministrationPageId } from "@reminder-tracker/domain";
import { useMutation, useQuery } from "convex/react";
import { useEffect, useRef, useState } from "react";
import { classifyCatalogueFailure } from "./failure";
import { CatalogueSuggestionReview } from "./suggestions";
import { CatalogueReports } from "./reports";
import { CatalogueInventory } from "./inventory";
import { CatalogueCreateDraft } from "./create-draft";
import { BundleAdministrationTasks } from "./bundle-tasks";
import { Goal2ProtectedReports } from "./goal-2-reports";
import { TimingRecommendationTasks } from "./timing-recommendations";
import {
  getCatalogueCapabilities,
  getCatalogueVersion,
  listCatalogueVersions,
  saveCatalogueDraft,
  transitionCatalogueVersion,
} from "../../backend/functions";
import {
  AdministrationImpactPanel,
  AdministrationPageHeader,
  AdministrationStatusSummary,
} from "../../ui/administration-surface";
import {
  formatAdministrationTime,
  parseAdministrationLocalDateTime,
} from "../../ui/administration-time";

// Operate: extend the existing warm-paper ruled register, not a new visual identity.
// Browse one bounded page, inspect one exact version, then explicitly review its action.
function key(prefix: "corr" | "cmd") {
  return `${prefix}_${crypto.randomUUID().replaceAll("-", "")}`;
}

export function CatalogueAdministrationPanel({
  page = "versions-publishing",
}: Readonly<{ page?: AdministrationPageId }> = {}): React.JSX.Element {
  const [correlationId] = useState(() => key("corr"));
  const [visitedPages, setVisitedPages] = useState<ReadonlySet<AdministrationPageId>>(
    () => new Set([page]),
  );
  useEffect(() => {
    setVisitedPages((current) =>
      current.has(page) ? current : new Set<AdministrationPageId>([...current, page]),
    );
  }, [page]);
  const capabilities = useQuery(getCatalogueCapabilities, { correlationId });
  const headings: Partial<Record<AdministrationPageId, string>> = {
    presets: "Presets",
    bundles: "Bundles",
    suggestions: "Suggestions",
    "versions-publishing": "Versions & publishing",
    "usage-reports": "Usage reports",
  };
  const purposes: Partial<Record<AdministrationPageId, string>> = {
    presets: "Review catalogue coverage and the latest admitted definition for each preset.",
    bundles: "Prepare and review related reminder packs before any controlled release.",
    suggestions:
      "Review customer suggestions and timing recommendations without exposing reminder content.",
    "versions-publishing":
      "Inspect exact catalogue versions and deliberately review publication actions.",
    "usage-reports": "Use privacy-protected aggregate reports to understand catalogue adoption.",
  };
  return (
    <section className="catalogue-governance" id="catalogue">
      <AdministrationPageHeader
        title={headings[page] ?? "Catalogue"}
        purpose={`${purposes[page] ?? "Work with reviewed catalogue definitions."} Existing customer reminders stay unchanged.`}
      />
      {capabilities === undefined ? <p role="status">Checking catalogue access…</p> : null}
      {visitedPages.has("presets") ? (
        <div hidden={page !== "presets"}>{capabilities ? <CatalogueInventory /> : null}</div>
      ) : null}
      {visitedPages.has("bundles") ? (
        <div hidden={page !== "bundles"}>
          <BundleAdministrationTasks />
        </div>
      ) : null}
      {visitedPages.has("suggestions") ? (
        <div hidden={page !== "suggestions"}>
          {capabilities?.canEditReview ? <CatalogueSuggestionReview /> : null}
          <TimingRecommendationTasks />
        </div>
      ) : null}
      {visitedPages.has("versions-publishing") ? (
        <div hidden={page !== "versions-publishing"}>
          {capabilities ? (
            capabilities.canEditReview || capabilities.canPublishWithdraw ? (
              <CatalogueBrowser capabilities={capabilities} />
            ) : (
              <p>Catalogue editing and publication are not enabled for your account.</p>
            )
          ) : null}
        </div>
      ) : null}
      {visitedPages.has("usage-reports") ? (
        <div hidden={page !== "usage-reports"}>
          {capabilities?.canReadAggregate ? <CatalogueReports /> : null}
          <Goal2ProtectedReports />
        </div>
      ) : null}
    </section>
  );
}

function CatalogueBrowser({
  capabilities,
}: {
  capabilities: CatalogueAdministrationCapabilities;
}): React.JSX.Element {
  const [state, setState] = useState<CatalogueVersionState | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [selected, setSelected] = useState<{ presetId: string; version: number } | null>(null);
  const [creating, setCreating] = useState(false);
  const [correlationId] = useState(() => key("corr"));
  const page = useQuery(
    listCatalogueVersions,
    selected === null && !creating ? { state, cursor, pageSize: 20, correlationId } : "skip",
  );
  if (creating && capabilities.canEditReview)
    return (
      <CatalogueCreateDraft
        onClose={() => setCreating(false)}
        onSaved={(value) => {
          setCreating(false);
          setSelected(value);
        }}
      />
    );
  if (selected !== null)
    return (
      <CatalogueVersion
        key={`${selected.presetId}/${selected.version}`}
        selected={selected}
        capabilities={capabilities}
        onClose={() => setSelected(null)}
        onSaved={setSelected}
      />
    );
  return (
    <>
      {page === undefined ? null : (
        <CatalogueQueueSummary
          page={page}
          {...(capabilities.canEditReview
            ? {
                onInspectVersion: (item: CatalogueVersionPage["items"][number]) =>
                  setSelected({ presetId: item.presetId, version: item.version }),
              }
            : {})}
        />
      )}
      {capabilities.canEditReview ? (
        <button type="button" onClick={() => setCreating(true)}>
          Create an initial draft
        </button>
      ) : null}
      <label>
        Version status{" "}
        <select
          value={state ?? "all"}
          onChange={(event) => {
            setState(
              event.target.value === "all" ? null : (event.target.value as CatalogueVersionState),
            );
            setCursor(null);
          }}
        >
          <option value="all">All statuses</option>
          {(["draft", "reviewed", "published", "withdrawn"] as const).map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
      </label>
      {page === undefined ? (
        <p role="status">Reading catalogue versions…</p>
      ) : (
        <>
          {page.items.length === 0 ? (
            <p>No versions on this page. Try another status or return to the first page.</p>
          ) : (
            <table className="timeline">
              <thead>
                <tr>
                  <th>Preset</th>
                  <th>Market</th>
                  <th>Version</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {page.items.map((item) => (
                  <tr key={`${item.presetId}/${item.version}`}>
                    <td data-label="Preset">{item.title}</td>
                    <td data-label="Market">{item.marketCode}</td>
                    <td data-label="Version">{item.version}</td>
                    <td data-label="Status">{item.state}</td>
                    <td data-label="Action">
                      <button
                        type="button"
                        onClick={() =>
                          setSelected({ presetId: item.presetId, version: item.version })
                        }
                      >
                        Inspect{" "}
                        <span className="sr-only">
                          {item.title} version {item.version}
                        </span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="push-policy-actions">
            <button type="button" disabled={cursor === null} onClick={() => setCursor(null)}>
              First page
            </button>
            <button
              type="button"
              disabled={page.nextCursor === null}
              onClick={() => setCursor(page.nextCursor)}
            >
              Next page
            </button>
          </div>
        </>
      )}
    </>
  );
}

export function CatalogueQueueSummary({
  page,
  onInspectVersion,
}: Readonly<{
  page: CatalogueVersionPage;
  onInspectVersion?: (item: CatalogueVersionPage["items"][number]) => void;
}>): React.JSX.Element {
  const draft = page.items.filter((item) => item.state === "draft");
  const reviewed = page.items.filter((item) => item.state === "reviewed");
  const published = page.items.filter((item) => item.state === "published");
  const firstDraft = draft[0];
  const firstReviewed = reviewed[0];
  const firstActionable = firstDraft ?? firstReviewed;
  return (
    <>
      <AdministrationStatusSummary
        items={[
          {
            label: "Needs action",
            tone: "action",
            value: draft.length,
            title: "Drafts to review",
            description: "Check wording and evidence before publication review.",
          },
          {
            label: "Monitor",
            tone: "monitor",
            value: reviewed.length,
            title: "Reviewed versions",
            description: "These still need a deliberate publication decision.",
          },
          {
            label: "Healthy",
            tone: "healthy",
            value: published.length,
            title: "Published versions",
            description: "These loaded versions are currently marked as published.",
          },
        ]}
      />
      <AdministrationImpactPanel
        label="Customer impact"
        title={
          draft.length > 0
            ? "Existing reminders stay unchanged while draft work is reviewed"
            : reviewed.length > 0
              ? "Reviewed wording does not reach customers until it is published"
              : "No unpublished catalogue work is visible on this page"
        }
        description={
          draft.length > 0
            ? `${draft.length} ${draft.length === 1 ? "draft is" : "drafts are"} visible on this loaded page.`
            : reviewed.length > 0
              ? `${reviewed.length} reviewed ${reviewed.length === 1 ? "version is" : "versions are"} awaiting a deliberate decision.`
              : "Inspect published or withdrawn history only when it supports the task at hand."
        }
        action={
          firstActionable !== undefined && onInspectVersion !== undefined ? (
            <button type="button" onClick={() => onInspectVersion(firstActionable)}>
              {firstDraft === undefined ? "Review publication decision" : "Review first draft"}
            </button>
          ) : undefined
        }
        evidence={
          <p>
            Counts describe this loaded page only. Publication changes catalogue guidance, never an
            existing customer reminder.
          </p>
        }
        tone={draft.length > 0 ? "action" : reviewed.length > 0 ? "monitor" : "healthy"}
      />
    </>
  );
}

function CatalogueVersion({
  selected,
  capabilities,
  onClose,
  onSaved,
}: {
  selected: { presetId: string; version: number };
  capabilities: CatalogueAdministrationCapabilities;
  onClose: () => void;
  onSaved: (value: { presetId: string; version: number }) => void;
}): React.JSX.Element {
  const [correlationId] = useState(() => key("corr"));
  const preview = useQuery(getCatalogueVersion, { ...selected, correlationId });
  if (preview === undefined) return <p role="status">Reading this version…</p>;
  return (
    <CatalogueVersionEditor
      preview={preview}
      version={selected.version}
      capabilities={capabilities}
      onClose={onClose}
      onSaved={onSaved}
    />
  );
}

type Pending =
  | { kind: "draft"; command: CatalogueDraftCommand }
  | { kind: "transition"; command: CatalogueTransitionCommand };
export function CatalogueVersionEditor({
  preview,
  version,
  capabilities,
  onClose,
  onSaved,
}: {
  preview: CatalogueVersionPreview;
  version: number;
  capabilities: CatalogueAdministrationCapabilities;
  onClose: () => void;
  onSaved: (value: { presetId: string; version: number }) => void;
}): React.JSX.Element {
  const saveDraft = useMutation(saveCatalogueDraft);
  const transition = useMutation(transitionCatalogueVersion);
  const [content, setContent] = useState(preview.content);
  const [startingRevision] = useState(preview.revision);
  const [editing, setEditing] = useState(false);
  const [from, setFrom] = useState("");
  const [until, setUntil] = useState("");
  const [approval, setApproval] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [securityFailure, setSecurityFailure] = useState<Error | null>(null);
  const pending = useRef<Pending | null>(null);
  if (securityFailure !== null) throw securityFailure;
  const stale = startingRevision !== preview.revision;
  const locked = busy || uncertain || stale;
  const send = async (intent: Pending) => {
    if (busy || (pending.current !== null && pending.current !== intent)) return;
    if (pending.current === null && stale) return;
    if (
      intent.kind === "draft" || intent.command.action === "review"
        ? !capabilities.canEditReview
        : !capabilities.canPublishWithdraw
    )
      return;
    pending.current = intent;
    setBusy(true);
    setFeedback(null);
    try {
      const result =
        intent.kind === "draft"
          ? await saveDraft(intent.command)
          : await transition(intent.command);
      pending.current = null;
      setUncertain(false);
      setEditing(false);
      setConfirmed(false);
      setFeedback(`Confirmed at version ${result.version}, revision ${result.revision}.`);
      onSaved({ presetId: content.presetId, version: result.version });
    } catch (error) {
      const failure = classifyCatalogueFailure(error);
      if (failure.kind === "security")
        setSecurityFailure(
          error instanceof Error
            ? error
            : Object.assign(new Error(failure.message), {
                data: { code: "REAUTHENTICATION_REQUIRED" },
              }),
        );
      if (failure.kind !== "unknown") pending.current = null;
      setUncertain(failure.kind === "unknown");
      setConfirmed(false);
      setFeedback(failure.message);
    } finally {
      setBusy(false);
    }
  };
  const act = (action: CatalogueTransitionCommand["action"]) => {
    if (!confirmed || locked) return;
    const start =
      action === "review"
        ? parseAdministrationLocalDateTime(from)
        : preview.review?.availableFromEpochMilliseconds;
    const end =
      action === "review"
        ? until === ""
          ? null
          : parseAdministrationLocalDateTime(until)
        : preview.review?.availableUntilEpochMilliseconds;
    if (
      start === undefined ||
      start === null ||
      !Number.isFinite(start) ||
      end === undefined ||
      (end !== null && (!Number.isFinite(end) || end <= start))
    ) {
      setFeedback("Choose a valid local start time and an optional later end before reviewing.");
      return;
    }
    void send({
      kind: "transition",
      command: {
        presetId: content.presetId,
        version,
        expectedRevision: preview.revision,
        contentDigest: preview.contentDigest,
        action,
        commandKey: key("cmd"),
        correlationId: key("corr"),
        availableFromEpochMilliseconds: start,
        availableUntilEpochMilliseconds: end,
        approvalReference: action === "publish" ? approval.trim() : null,
      },
    });
  };
  return (
    <div>
      <button type="button" disabled={busy || uncertain} onClick={onClose}>
        Back to catalogue
      </button>
      {stale ? (
        <p role="status">
          This version has changed. Your local wording has not been replaced. Return to the
          catalogue and inspect the latest revision before another change.
        </p>
      ) : null}
      <h3>{preview.content.title}</h3>
      <p>
        Version {version} · Revision {preview.revision} · {preview.state} · {content.marketCode}
      </p>
      <CatalogueEditorialPreview preview={preview} />
      {capabilities.canEditReview && !editing ? (
        <button
          type="button"
          disabled={locked}
          onClick={() => {
            setEditing(true);
            setConfirmed(false);
          }}
        >
          {preview.state === "draft" ? "Edit draft" : "Create next draft version"}
        </button>
      ) : null}
      {editing ? (
        <fieldset disabled={locked || !capabilities.canEditReview}>
          <legend>Catalogue wording</legend>
          <p>
            Identity, type and timing rules stay unchanged. A saved edit must be reviewed again.
          </p>
          {(
            [
              "title",
              "applicability",
              "dateLabel",
              "dateHelper",
              "timingExplanation",
              "interpretation",
              "publicationBasis",
            ] as const
          ).map((field) => (
            <label key={field} style={{ display: "block", marginBlock: "16px" }}>
              {
                {
                  title: "Title",
                  applicability: "Who this is for",
                  dateLabel: "Date label",
                  dateHelper: "Date guidance",
                  timingExplanation: "Timing explanation",
                  interpretation: "How to interpret this guidance",
                  publicationBasis: "Publication basis",
                }[field]
              }
              <textarea
                rows={3}
                value={content[field]}
                maxLength={field === "title" ? 120 : 1000}
                style={{ display: "block", width: "100%", boxSizing: "border-box" }}
                onChange={(event) => setContent({ ...content, [field]: event.target.value })}
              />
            </label>
          ))}
          {(["aliases", "helpfulTips", "thingsToConsider"] as const).map((field) => (
            <label key={field} style={{ display: "block", marginBlock: "16px" }}>
              {
                {
                  aliases: "Search aliases",
                  helpfulTips: "Helpful tips",
                  thingsToConsider: "Things to consider",
                }[field]
              }{" "}
              (one per line, up to 12)
              <textarea
                rows={4}
                value={content[field].join("\n")}
                maxLength={6012}
                style={{ display: "block", width: "100%", boxSizing: "border-box" }}
                onChange={(event) =>
                  setContent({
                    ...content,
                    [field]: event.target.value === "" ? [] : event.target.value.split("\n"),
                  })
                }
              />
            </label>
          ))}
          <label style={{ display: "block", marginBlock: "16px" }}>
            Excluded claim (optional)
            <textarea
              rows={3}
              value={content.excludedClaim ?? ""}
              maxLength={1000}
              style={{ display: "block", width: "100%", boxSizing: "border-box" }}
              onChange={(event) =>
                setContent({
                  ...content,
                  excludedClaim: event.target.value === "" ? null : event.target.value,
                })
              }
            />
          </label>
          <label>
            Content checked on
            <input
              type="date"
              value={content.checkedOn}
              onChange={(event) => setContent({ ...content, checkedOn: event.target.value })}
            />
          </label>
          <label>
            Next content review
            <input
              type="date"
              value={content.nextReviewOn}
              onChange={(event) => setContent({ ...content, nextReviewOn: event.target.value })}
            />
          </label>
          <fieldset>
            <legend>Sources</legend>
            {content.sources.map((source, index) => (
              <div key={index}>
                <label>
                  Source {index + 1} reference
                  <input
                    maxLength={64}
                    value={source.id}
                    onChange={(event) =>
                      setContent({
                        ...content,
                        sources: content.sources.map((row, i) =>
                          i === index ? { ...row, id: event.target.value } : row,
                        ),
                      })
                    }
                  />
                </label>
                <label>
                  Source {index + 1} title
                  <input
                    maxLength={250}
                    value={source.title}
                    onChange={(event) =>
                      setContent({
                        ...content,
                        sources: content.sources.map((row, i) =>
                          i === index ? { ...row, title: event.target.value } : row,
                        ),
                      })
                    }
                  />
                </label>
                <label>
                  Source {index + 1} URL
                  <input
                    type="url"
                    maxLength={2000}
                    value={source.url}
                    onChange={(event) =>
                      setContent({
                        ...content,
                        sources: content.sources.map((row, i) =>
                          i === index ? { ...row, url: event.target.value } : row,
                        ),
                      })
                    }
                  />
                </label>
                <button
                  type="button"
                  disabled={content.sources.length <= 1}
                  onClick={() =>
                    setContent({
                      ...content,
                      sources: content.sources.filter((_, i) => i !== index),
                    })
                  }
                >
                  Remove source {index + 1}
                </button>
              </div>
            ))}
            <button
              type="button"
              disabled={content.sources.length >= 12}
              onClick={() =>
                setContent({
                  ...content,
                  sources: [...content.sources, { id: "", title: "", url: "" }],
                })
              }
            >
              Add source
            </button>
          </fieldset>
          <button
            type="button"
            onClick={() =>
              void send({
                kind: "draft",
                command: {
                  content,
                  version: preview.state === "draft" ? version : null,
                  expectedRevision: preview.state === "draft" ? preview.revision : null,
                  commandKey: key("cmd"),
                  correlationId: key("corr"),
                },
              })
            }
          >
            Save draft only
          </button>
          <button
            type="button"
            onClick={() => {
              setContent(preview.content);
              setEditing(false);
            }}
          >
            Cancel wording changes
          </button>
        </fieldset>
      ) : null}
      {!editing && preview.state === "draft" && capabilities.canEditReview ? (
        <fieldset disabled={locked}>
          <legend>Review publication window</legend>
          <label>
            Available from (your local time)
            <input
              type="datetime-local"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setConfirmed(false);
              }}
            />
          </label>
          <label>
            Available until (your local time, optional)
            <input
              type="datetime-local"
              value={until}
              onChange={(e) => {
                setUntil(e.target.value);
                setConfirmed(false);
              }}
            />
          </label>
        </fieldset>
      ) : null}
      {!editing && preview.state === "reviewed" && capabilities.canPublishWithdraw ? (
        <label>
          Publication approval reference
          <input
            maxLength={160}
            value={approval}
            disabled={locked}
            onChange={(e) => {
              setApproval(e.target.value);
              setConfirmed(false);
            }}
          />
        </label>
      ) : null}
      {!editing &&
      ((preview.state === "draft" && capabilities.canEditReview) ||
        ((preview.state === "reviewed" || preview.state === "published") &&
          capabilities.canPublishWithdraw)) ? (
        <>
          <label style={{ display: "block", minHeight: 48 }}>
            <input
              type="checkbox"
              checked={confirmed}
              disabled={locked}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            I have checked this exact version and its publication window.
          </label>
          {preview.state === "draft" ? (
            <button type="button" disabled={locked || !confirmed} onClick={() => act("review")}>
              Mark this version reviewed
            </button>
          ) : preview.state === "reviewed" ? (
            <button
              type="button"
              disabled={locked || !confirmed || approval.trim() === ""}
              onClick={() => act("publish")}
            >
              Publish reviewed version
            </button>
          ) : (
            <button type="button" disabled={locked || !confirmed} onClick={() => act("withdraw")}>
              Withdraw from new selections
            </button>
          )}
        </>
      ) : null}
      {feedback !== null ? <p role={uncertain ? "alert" : "status"}>{feedback}</p> : null}
      {busy ? (
        <p role="status">Waiting for confirmation…</p>
      ) : uncertain ? (
        <button
          type="button"
          onClick={() => {
            if (pending.current !== null) void send(pending.current);
          }}
        >
          Retry same request
        </button>
      ) : null}
    </div>
  );
}

function CatalogueEditorialPreview({
  preview,
}: {
  preview: CatalogueVersionPreview;
}): React.JSX.Element {
  const c = preview.content;
  return (
    <div>
      <p>{c.applicability}</p>
      <dl>
        <dt>Type</dt>
        <dd>{c.serviceTypeId}</dd>
        <dt>Date</dt>
        <dd>
          {c.dateLabel} — {c.dateHelper}
        </dd>
        <dt>Content checked</dt>
        <dd>
          {c.checkedOn}; review due {c.nextReviewOn}
        </dd>
      </dl>
      <h4>Suggested timing</h4>
      <p>{c.timingExplanation}</p>
      {c.timingProfiles.map((profile) => (
        <p key={profile.id}>
          {profile.purpose}:{" "}
          {profile.offsets.map((offset) => `${offset.amount} ${offset.unit} before`).join(", ")} at{" "}
          {profile.localTime}. {profile.basisBoundary}
        </p>
      ))}
      <h4>Helpful tips</h4>
      <ul>
        {c.helpfulTips.map((tip, i) => (
          <li key={i}>{tip}</li>
        ))}
      </ul>
      <h4>Things to consider</h4>
      <ul>
        {c.thingsToConsider.map((tip, i) => (
          <li key={i}>{tip}</li>
        ))}
      </ul>
      <details>
        <summary>Sources and publication evidence</summary>
        <p>{c.publicationBasis}</p>
        <p>{c.interpretation}</p>
        {c.excludedClaim === null ? null : <p>Excluded claim: {c.excludedClaim}</p>}
        <ul>
          {c.sources.map((source) => (
            <li key={source.id}>
              {source.title} — {source.url}
            </li>
          ))}
        </ul>
        <p>Content digest: {preview.contentDigest}</p>
      </details>
      {preview.review === null ? (
        <p>Not reviewed for publication.</p>
      ) : (
        <p>
          Reviewed window: {formatAdministrationTime(preview.review.availableFromEpochMilliseconds)}{" "}
          to{" "}
          {preview.review.availableUntilEpochMilliseconds === null
            ? "no scheduled end"
            : formatAdministrationTime(preview.review.availableUntilEpochMilliseconds)}
          .
        </p>
      )}
    </div>
  );
}
